import type { Palette, PriceStep } from './types';

type PricingProps = { palette: Palette; priceSteps: PriceStep[] };

export function PricingSection({ palette, priceSteps }: PricingProps) {
  return (
    <section
      id="pricing"
      className="relative overflow-hidden px-6 py-16 scroll-mt-24 md:scroll-mt-28 lg:px-12"
      style={{ 
        // Using a deep gradient to contrast with the previous light section
        background: `linear-gradient(135deg, ${palette.forest} 0%, #1a2e25 100%)`,
        color: palette.white 
      }}
    >
      {/* --- BACKGROUND DEPTH --- */}
      {/* A large subtle glow behind the main card */}
      <div 
        className="absolute left-1/2 top-1/2 h-[800px] w-[800px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-20 blur-[120px]"
        style={{ background: palette.sage }}
      />
      
      {/* Floating accents */}
      <div className="absolute right-0 top-0 h-64 w-64 translate-x-1/2 -translate-y-1/2 rounded-full bg-amber-500/10 blur-3xl" />
      <div className="absolute left-0 bottom-0 h-64 w-64 -translate-x-1/2 translate-y-1/2 rounded-full bg-emerald-500/10 blur-3xl" />

      <div className="relative mx-auto max-w-7xl">
        
        {/* MAIN CARD CONTAINER */}
        <div className="rounded-[40px] border border-white/10 bg-white/5 p-8 shadow-2xl backdrop-blur-xl lg:p-12">
          
          {/* HEADER: Inline Layout for tightness */}
          <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
            
            {/* Left: Value Proposition */}
            <div className="max-w-2xl space-y-4">
              <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-amber-200 shadow-[0_0_15px_rgba(217,119,6,0.2)]">
                 <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse" />
                 Unbeatable Value
              </div>
              
              <h2 className="text-3xl font-bold leading-tight sm:text-4xl text-white">
                We’re confident we have <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-200 to-amber-500">
                  the best price.
                </span>
              </h2>
              
              <p className="text-lg text-emerald-100/80 leading-relaxed">
                We don't expect you to find a lower price for this level of quality, but on the off chance you do?
                <span className="text-white font-bold"> We match it. </span> No questions asked.
              </p>
            </div>
            
            {/* Right: The "Pop" Button */}
            <div className="shrink-0">
               <a
                href="/home-pros/price-match"
                className="group relative inline-flex items-center justify-center overflow-hidden rounded-full px-8 py-4 text-sm font-bold uppercase tracking-[0.2em] text-white shadow-[0_0_40px_-10px_rgba(217,119,6,0.5)] transition-transform hover:-translate-y-1"
                style={{ 
                  background: `linear-gradient(to right, ${palette.clay}, #b45309)` 
                }}
              >
                <span className="relative z-10 flex items-center gap-2">
                  Challenge Us
                  <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                </span>
                {/* Shine Effect */}
                <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent group-hover:animate-shimmer" />
              </a>
              <p className="mt-3 text-center text-[10px] text-emerald-200/60 uppercase tracking-wider">
                100% Price Match Guarantee
              </p>
            </div>
          </div>

          {/* STEPS GRID */}
          <div className="mt-12 grid gap-6 sm:grid-cols-3">
            {priceSteps.map((step, idx) => (
              <div 
                key={step.title} 
                className="group relative overflow-hidden rounded-3xl bg-white p-6 shadow-lg transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-emerald-900/20"
              >
                {/* Decoration: Colorful top border */}
                <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-emerald-400 to-emerald-600 opacity-80" />
                
                {/* Big Number in Background */}
                <p className="absolute -right-4 -top-6 text-9xl font-black text-slate-100 select-none transition-colors group-hover:text-emerald-50">
                  {idx + 1}
                </p>
                
                <div className="relative z-10">
                  <div className="mb-4 inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-xs font-bold text-emerald-800">
                    {idx + 1}
                  </div>
                  
                  <h3 className="text-xl font-bold text-slate-900 group-hover:text-emerald-800 transition-colors">
                    {step.title}
                  </h3>
                  
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">
                    {step.copy}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* FOOTER */}
          <div className="mt-8 flex flex-col gap-4 border-t border-white/10 pt-6 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-emerald-200/50 italic">
              *Matches identical scope, materials, and warranty from licensed contractors.
            </p>
            <div className="flex items-center gap-2 rounded-full bg-white/5 px-4 py-1.5 backdrop-blur-sm border border-white/10">
               <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
               <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-100">
                 Financing Available
               </p>
            </div>
          </div>
          
        </div>
      </div>
    </section>
  );
}
